import React, { useEffect } from "react";
import { Link, useLocation, useOutletContext } from "react-router-dom";
import { CheckCircle2, Phone, ArrowRight, Clock, HeartHandshake, MessageCircle } from "lucide-react";

const CONTAINER = "max-w-3xl mx-auto px-4 sm:px-6 lg:px-8";
const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

export default function ThankYou() {
  const location = useLocation();
  const { content, openChat } = useOutletContext();
  const state = location.state || {};
  const name = state.name;
  const chat = state.chat;
  const ty = state.thank_you || null;
  const phone = (ty && ty.phone) || content?.phone;

  useEffect(() => {
    document.title = "Thank You · MovingMyMama";
    window.scrollTo({ top: 0 });
  }, []);

  const headline = ty?.headline || (name ? `Thank you, ${name}.` : "Thank you.");
  const message = ty?.message || `Your request is in good hands. ${chat ? "A guide will reach out to start your chat shortly." : "A caring guide will be in touch soon — usually the same day."}`;
  const steps = (ty?.next_steps && ty.next_steps.length)
    ? ty.next_steps.map((body, i) => ({ icon: [HeartHandshake, Clock, CheckCircle2][i % 3], title: ["We listen first", "We reach out soon", "You get a plan"][i % 3], body }))
    : [
        { icon: HeartHandshake, title: "We listen first", body: "No pressure, ever. We start by understanding your family." },
        { icon: Clock, title: "We reach out soon", body: "Expect a friendly call or email, usually the same day." },
        { icon: CheckCircle2, title: "You get a plan", body: "A clear, personalized path forward you can feel good about." },
      ];

  return (
    <div data-testid="thank-you-page" className="py-20 sm:py-28">
      <div className={CONTAINER}>
        <div className="text-center mmm-fade-up">
          <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-[#2F7D5A]/12 text-[#2F7D5A]">
            <CheckCircle2 size={44} />
          </span>
          <h1 data-testid="thankyou-headline" className="mt-7 text-4xl sm:text-5xl font-display font-bold tracking-tight text-[#1C4358] leading-tight">
            {headline}
          </h1>
          <p data-testid="thankyou-message" className="mt-5 text-xl text-[#252221] leading-relaxed max-w-2xl mx-auto">
            {message}
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-3 text-center">
          {steps.map((s, i) => (
            <div key={i} className="rounded-[0.9rem] bg-[#FBF6EC] border border-[#1C4358]/10 p-6">
              <s.icon size={26} className="mx-auto text-[#9C1E22]" />
              <h3 className="mt-3 font-display font-semibold text-[#1C4358]">{s.title}</h3>
              <p className="mt-1.5 text-[15px] text-[#4B5563] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        {/* suggested next steps */}
        {ty?.service_lane_slug && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to={`/services/${ty.service_lane_slug}`} data-testid="thankyou-lane-link"
              className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#1C4358] hover:underline">
              Explore {ty.service_lane_label || "how we help"} <ArrowRight size={15} />
            </Link>
          </div>
        )}

        <div className="mt-10 rounded-[1.2rem] bg-[#1C4358] mmm-grain px-6 py-10 text-center">
          <p className="text-lg text-white/90">Need to talk to someone right now?</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            {phone && (
              <a href={telHref(phone)} data-testid="thankyou-call-btn"
                className="mmm-tap inline-flex items-center gap-2 px-7 h-14 rounded-full text-[17px] font-semibold text-[#1C4358] bg-[#DEE029] hover:bg-[#c9cb17] transition-colors">
                <Phone size={19} /> Call {phone}
              </a>
            )}
            {openChat && (
              <button type="button" data-testid="thankyou-chat-btn" onClick={() => openChat({ cta_source: "Thank You Chat" })}
                className="mmm-tap inline-flex items-center gap-2 px-6 h-14 rounded-full text-[17px] font-semibold text-white border-2 border-white/70 hover:bg-white/10 transition-colors">
                <MessageCircle size={19} /> Start a chat
              </button>
            )}
          </div>
          <div className="mt-6">
            <Link to="/" data-testid="thankyou-home-btn" className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-[15px] font-medium transition-colors">
              Back to home <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
